import { DeleteOutline, InfoOutlined } from '@mui/icons-material';
import {
  Mode,
  OpeningHours,
  ShopLocationType,
} from '../../../interfaces/delivery-company/pickups';
import {
  RadioGroup,
  FormControlLabel,
  Radio,
  Card,
  Checkbox,
  Chip,
  IconButton,
  Alert,
  alpha,
  Paper,
  styled,
  Tooltip,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup, DialogContent, DialogTitle,
} from '@mui/material';
import {
  Box,
  Button,
  Switch,
  Stack,
  TextField,
  Typography,
  Divider,
} from '@mui/material';
import { LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import React, { useContext, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import axios, { AxiosResponse } from 'axios';
import { API_ENDPOINT } from '../../../configurations/global.config';
import { useSnackBar } from '../../../contexts/snackbar';
import { AuthContext } from '../../../contexts/user-context/user-context';
import CustomDialog from '../../common/custom-dialog/custom-dialog';
import { isMobile, isTablet } from 'react-device-detect';
import ClearIcon from '@mui/icons-material/Clear';
import { ScheduleEditor } from './schedule-editor';
import { ensureOpeningHours } from './schedule-components';
import { TimezoneSelector } from './timezone-selector';



type ModeCellProps = {
  pickup: ShopLocationType;
  loading?: boolean;
  onModeChange: (next: Mode) => void;
  onScheduleSubmit: () => void;
};

export function ModeCell({ pickup, loading = false, onModeChange,onScheduleSubmit }: ModeCellProps) {
  const [openScheduleDialog, setOpenScheduleDialog] =
    useState<boolean>(false);
  const [pickupData, setPickupData] = useState<ShopLocationType>({...pickup,opening_hours: ensureOpeningHours(pickup.opening_hours)});
  const [scheduleHasError, setScheduleHasError] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { config } = useContext(AuthContext);
  const { showMessage } = useSnackBar();

  const handleSubmit = async () => {
    if (pickupData) {
      setIsLoading(true);
      const requestData = {...pickupData,mode:'SCHEDULE'};

      try {
        const response =   await axios.put(
            `${API_ENDPOINT}/api/v1/pickup/${pickupData.id}/`,
            requestData,
            config
          );
        
        if (response.status >= 200 && response.status < 300) {
          setOpenScheduleDialog(false);
          setScheduleHasError(false);
          onScheduleSubmit();
        }
      } catch (e) {
        if (axios.isAxiosError(e)) {
          showMessage(
            'Failed to save pickup location: ' + e.response?.statusText,'error'
          );
        } else {
          showMessage('Failed to save pickup location','error');
        }
        console.log(e);
      }
      setIsLoading(false);
    }
  };


  const baseBtnSx = {
    textTransform: 'none',
    px: 1.25,
    borderRadius: 1.5,
  };

  const openSx = {
    ...baseBtnSx,
    '&.Mui-selected, &.Mui-selected:hover': {
      bgcolor: 'success.main',
      color: 'success.contrastText',
      borderColor: 'success.main',
    },
  };

  const closeSx = {
    ...baseBtnSx,
    '&.Mui-selected, &.Mui-selected:hover': {
      bgcolor: 'error.main',
      color: 'error.contrastText',
      borderColor: 'error.main',
    },
  };

  const scheduleSx = {
    ...baseBtnSx,
    '&.Mui-selected, &.Mui-selected:hover': {
      bgcolor: 'info.main',
      color: 'info.contrastText',
      borderColor: 'info.main',
    },
  };
  return (
    <>
      <Stack direction="column" alignItems="center" justifyContent="center">
        <ToggleButtonGroup
          size="small"
          exclusive
          value={pickup.mode ?? 'SCHEDULE'}
          onChange={(_, next: Mode | null) => next && onModeChange(next)}
        >
          <ToggleButton value="OPEN" disabled={loading} sx={openSx}>
            Open Shop

          </ToggleButton>
          {pickup.opening_hours ? (
            <ToggleButton value="SCHEDULE" disabled={loading} sx={scheduleSx}>
              Follow Schedule
            </ToggleButton>
          ) : null}
          <ToggleButton value="CLOSED" disabled={loading} sx={closeSx}>
            Close Shop
          </ToggleButton>

        </ToggleButtonGroup>
        {loading && <CircularProgress size={16} sx={{ ml: 1 }} />}
        <Box sx={{ mt: 1 }}>
          {!pickup.opening_hours ? (
            <Button
              size="small"
              onClick={() => setOpenScheduleDialog(true)}
            >
              Add Schedule
            </Button>
          ) : (
            <Button
              size="small"
              onClick={() => setOpenScheduleDialog(true)}
            >
              Edit/View Schedule
            </Button>
          )}
        </Box>
      </Stack>

      {openScheduleDialog && (
        <CustomDialog
          fullWidth
          open={openScheduleDialog}
          onClose={() => setOpenScheduleDialog(false)}
          fullScreen={(isMobile && !isTablet)}
          maxWidth="lg"
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 2 }}>
            <DialogTitle sx={{ ml: 2, p: 2 }}>
                <Typography variant="h6">Pickup Location Schedule</Typography>
            </DialogTitle>
            <IconButton onClick={() => setOpenScheduleDialog(false)}>
              <ClearIcon color="primary" fontSize="large" />
            </IconButton>
          </Box>
          <DialogContent>
            {pickupData.opening_hours && (
              <>
                <TimezoneSelector
                  value={pickupData.utc_offset}
                  onChange={(offset) => setPickupData({ ...pickupData, utc_offset: offset })}
                />
                <ScheduleEditor
                  value={pickupData.opening_hours? pickupData.opening_hours : ensureOpeningHours(pickup.opening_hours)}
                  onChange={(oh) =>
                    setPickupData({ ...pickupData, opening_hours: oh })
                  }
                  onValidityChange={(hasErr) => setScheduleHasError(hasErr)}
                />
              </>

            )}
            <Button
              size={isMobile && !isTablet ? 'small' : 'medium'}
              variant="contained"
              disabled={scheduleHasError || isLoading}
              onClick={()=>handleSubmit()}
            >
              Save
            </Button>
          </DialogContent>
        </CustomDialog>
      )}
    </>

  );
}

export function IsOpenCell({ pickup }: { pickup: ShopLocationType }) {
  const label = pickup.is_open ? 'Open' : 'Closed';
  const color = pickup.is_open ? 'success' : 'default';
  return (
    <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
      <Chip size="small" label={label} color={color as any} />
    </Stack>
  );
}

